import { Circle, Stroke } from 'ol/style';
import { containsCoordinate } from 'ol/extent';
import Fill from 'ol/style/Fill';
import { fromLonLat } from 'ol/proj';
import Icon from 'ol/style/Icon';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';

import { degreesToRadians } from 'utils/map/utils';
import { hasArrayJustOneElement } from 'utils/main';
import {
  UAVO_ICON_COLOR,
  UAVO_ICON_COLOR_SELECTED,
  UAVO_ICON_SCALE,
  UAVO_ICONS_BACK,
  UAVO_ICONS_TEXT,
  UAVO_NON_ROTABLE,
  UAVOS_ICONS,
  UAVO_ICON_SCALE_SELECTED,
  UAVO_TYPE_FALLBACK,
  UTM_ICON_COLOR,
} from 'consts/mapConfig';
import UserSessionDataHandler from 'auth/UserSessionsDataHandler';

const getUavoIcon = (type, mappingUavos) =>
  mappingUavos[type] ||
  mappingUavos[UAVO_TYPE_FALLBACK[type]] ||
  UAVOS_ICONS[type] ||
  UAVOS_ICONS.ufo;

const getAltitude = uavoData => {
  const alt = uavoData?.altitude_amsl ?? uavoData?.altitude ?? uavoData?.baro_altitude;
  return alt != null ? Number(alt).toFixed(1) : '—';
};

const iffColorPicker = iff => {
  if (iff === 'FOE') return UAVO_ICONS_BACK.enemy;
  if (iff === 'FRIEND') return UAVO_ICONS_BACK.friend;

  return UAVO_ICONS_BACK.noColor;
};

const basicUavoStyle = (uavoData, mappingUavos) => {
  const rotationInRadians = returnUavoRotation(uavoData.track, uavoData.type);
  const mapTiles = UserSessionDataHandler.getFromUserSettingsObjectInStore(
    UserSessionDataHandler.defaultTiles
  );

  return [
    new Style({
      image: new Circle({
        fill: new Fill({
          color: iffColorPicker(uavoData),
        }),
        stroke: new Stroke({
          color: UAVO_ICONS_BACK.stroke,
          width: UAVO_ICONS_BACK.strokeWidth,
        }),
        radius: UAVO_ICONS_BACK.radius,
      }),
      text: new Text({
        text: `${uavoData.id || 'N/A'}\n↑${getAltitude(uavoData)}m  →${uavoData?.speed?.toFixed(1) || '—'}km/h`,
        font: '600 10px "Inter", "Segoe UI", sans-serif',
        textAlign: 'left',
        textBaseline: 'bottom',
        offsetY: -14,
        offsetX: 8,

        fill: new Fill({
          color: 'rgba(226, 232, 240, 0.95)',
        }),

        backgroundFill: new Fill({
          color: 'rgba(15, 23, 42, 0.85)',
        }),

        backgroundStroke: new Stroke({
          color: 'rgba(255, 255, 255, 0.12)',
          width: 0.5,
        }),

        padding: [3, 5, 3, 5],
      }),
      // text: new Text({
      //   text: returnProperPlaneParameter(uavoData),
      //   scale: 1.2,
      //   font: 'bold 8px sans-serif',
      //   textAlign: 'center',
      //   textBaseline: 'bottom',
      //   offsetY: 20,
      //   fill: new Fill({
      //     color: UAVO_ICON_COLOR[mapTiles] || UAVO_ICON_COLOR.default,
      //   }),
      // }),
    }),
    new Style({
      fill: new Fill({ color: UAVO_ICONS_TEXT.color }),
      image: new Icon({
        src: getUavoIcon(uavoData.type, mappingUavos),
        scale: UAVO_ICON_SCALE,
        color: uavoData.source === 'UTM' ? UTM_ICON_COLOR : (UAVO_ICON_COLOR[mapTiles] || UAVO_ICON_COLOR.default),
        rotation: rotationInRadians,
      }),
    }),
  ];
};

const selectedUavoStyle = (uavoData, mappingUavos) => {
  const rotationInRadians = returnUavoRotation(uavoData.track, uavoData.type);

  return [
    new Style({
      image: new Circle({
        fill: new Fill({
          color: iffColorPicker(uavoData),
        }),
        stroke: new Stroke({
          color: UAVO_ICONS_BACK.stroke,
          width: UAVO_ICONS_BACK.strokeWidth,
        }),
        radius: UAVO_ICONS_BACK.radius,
      }),
      text: new Text({
        text: `${uavoData.id || 'N/A'}\n↑${getAltitude(uavoData)}m  →${uavoData?.speed?.toFixed(1) || '—'}km/h`,
        font: '600 10px "Inter", "Segoe UI", sans-serif',
        textAlign: 'left',
        textBaseline: 'bottom',
        offsetY: -14,
        offsetX: 8,

        fill: new Fill({
          color: UAVO_ICON_COLOR_SELECTED,
        }),

        backgroundFill: new Fill({
          color: 'rgba(15, 23, 42, 0.9)',
        }),

        backgroundStroke: new Stroke({
          color: 'rgba(255, 165, 0, 0.3)',
          width: 0.5,
        }),

        padding: [3, 5, 3, 5],
      }),
      // text: new Text({
      //   text: returnProperPlaneParameter(uavoData),
      //   scale: 1.2,
      //   font: 'bold 8px sans-serif',
      //   textAlign: 'center',
      //   textBaseline: 'bottom',
      //   offsetY: 20,
      //   fill: new Fill({
      //     color: UAVO_ICON_COLOR_SELECTED,
      //   }),
      // }),
    }),
    new Style({
      fill: new Fill({ color: UAVO_ICONS_TEXT.color }),
      image: new Icon({
        src: getUavoIcon(uavoData.type, mappingUavos),
        color: UAVO_ICON_COLOR_SELECTED,
        scale: UAVO_ICON_SCALE_SELECTED,
        rotation: rotationInRadians,
      }),
    }),
  ];
};

const iffColorStyle = iff =>
  new Circle({
    fill: new Fill({
      color: iffColorPicker(iff),
    }),
    stroke: new Stroke({
      color: UAVO_ICONS_BACK.stroke,
      width: UAVO_ICONS_BACK.strokeWidth,
    }),
    radius: UAVO_ICONS_BACK.radius,
  });

// const getBasicImageStyle = (uavoData, mappingUavos) => {
//   const rotationInRadians =
//     uavoData.track && mappingUavos[uavoData.type]
//       ? degreesToRadians(uavoData)
//       : 0;
//   const mapTiles = UserSessionDataHandler.getFromUserSettingsObjectInStore(
//     UserSessionDataHandler.defaultTiles
//   );

//   return new Icon({
//     src: getUavoIcon(uavoData.type, mappingUavos),
//     scale: UAVO_ICON_SCALE,
//     color: UAVO_ICON_COLOR[mapTiles] || UAVO_ICON_COLOR.default,
//     rotation: rotationInRadians,
//   });
// };

// const getSelectedImageStyle = (uavoData, mappingUavos) => {
//   const rotationInRadians =
//     uavoData.track && mappingUavos[uavoData.type]
//       ? degreesToRadians(uavoData)
//       : 0;
//   return new Icon({
//     src: getUavoIcon(uavoData.type, mappingUavos),
//     color: UAVO_ICON_COLOR_SELECTED,
//     scale: UAVO_ICON_SCALE_SELECTED,
//     rotation: rotationInRadians,
//   });
// };

const setIFFColorIfChanged = uavoFeature => {
  const { iff } = uavoFeature.getProperties().data;
  const iffStyle = uavoFeature.getStyle()[0];
  if (
    iffStyle
      ?.getImage()
      ?.getFill()
      ?.getColor() !== iffColorPicker(iff)
  )
    iffStyle.setImage(iffColorStyle(iff));
};

const setSelectedUavoStyle = (selectedUavo, mappingUavos) => {
  const uavoData = selectedUavo.getProperties().data;
  console.log('uavo', uavoData)
  selectedUavo.setStyle(selectedUavoStyle(uavoData, mappingUavos));
};

const setBasicUavoStyle = (selectedUavo, mappingUavos) => {
  const uavoData = selectedUavo.getProperties().data;
  selectedUavo.setStyle(basicUavoStyle(uavoData, mappingUavos));
};

const setBasicUavoIconStyle = (selectedUavo, mappingUavos) => {
  const uavoData = selectedUavo.getProperties().data;
  selectedUavo.setStyle(basicUavoStyle(uavoData, mappingUavos));
};

const updateSelectedVehicleData = (selectedUavo, uavos) => {
  const id = selectedUavo.getProperties().data.id
    ? selectedUavo.getProperties().data.id
    : selectedUavo.getProperties().data.uid;
  const selectedVehicleNewObject = uavos.find(x => x.id === id || x.uid === id);
  if (selectedVehicleNewObject)
    selectedUavo.setProperties({ data: selectedVehicleNewObject });
};

const getFeatureByIdFromLayer = (layer, featureId) =>
  layer.getSource() ? layer.getSource().getFeatureById(featureId) : null;

const showPopup = (popupOverlay, coordinates) => {
  popupOverlay.setPosition(coordinates);
};

const centerMapToCurrentUavoPosition = (map, selectedUavos) => {
  if (!hasArrayJustOneElement(selectedUavos)) return;
  map.getView().setCenter(selectedUavos[0].getGeometry().getCoordinates());
};

const removeSelectedUavosIfDisapear = (selectedUavos, currentUavos) => {
  const updatedSelectedUavos = selectedUavos.filter(uavo =>
    isUavoStillActive(uavo, currentUavos)
  );

  return updatedSelectedUavos;
};

const isUavoStillActive = (uavoFeature, allCurrentVehicles) => {
  const id = uavoFeature.values_.data.id
    ? uavoFeature.values_.data.id
    : uavoFeature.values_.data.uid;
  return !!allCurrentVehicles.find(
    currentFeature => currentFeature.id === id || currentFeature.uid === id
  );
};

const returnUavoRotation = (track, type) => {
  const azimuthOfUnrotableFigures = 0;
  let rotation = degreesToRadians(track);
  if (!type || !track) rotation = degreesToRadians(azimuthOfUnrotableFigures);

  if (UAVO_NON_ROTABLE.includes(type))
    rotation = degreesToRadians(azimuthOfUnrotableFigures);
  return rotation;
};

const setUavoRotation = uavoFeature => {
  const { track, type } = uavoFeature.getProperties().data;
  if (uavoFeature.getStyle())
    uavoFeature
      .getStyle()[1]
      .getImage()
      .setRotation(returnUavoRotation(track, type));
};

const parseWeatherString = weatherString =>
  `${weatherString
    .split(' ')
    .join('_')
    .toLowerCase()}_`;

const windDegreesParser = degrees => {
  if (degrees >= 0 && degrees < 22.5) {
    return 'north_';
  }
  if (degrees >= 337.5 && degrees < 360) {
    return 'north_';
  }
  if (degrees >= 22.5 && degrees < 67.5) {
    return 'northEast_';
  }
  if (degrees >= 67.5 && degrees < 112.5) {
    return 'east_';
  }
  if (degrees >= 112.5 && degrees < 157.5) {
    return 'southEast_';
  }
  if (degrees >= 157.5 && degrees < 202.5) {
    return 'south_';
  }
  if (degrees >= 202.5 && degrees < 247.5) {
    return 'southWest_';
  }
  if (degrees >= 247.5 && degrees < 292.5) {
    return 'west_';
  }
  if (degrees >= 292.5 && degrees < 337.5) {
    return 'northWest_';
  }
};

export const getAllUavosInsideExtent = (allUavos, extent) => {
  if (!extent || !allUavos.length) return [];
  const uavosInside = [];
  for (const uavo of allUavos) {
    const coordinates = uavo.position.coordinates
      ? uavo.position.coordinates
      : uavo.position;
    if (containsCoordinate(extent, fromLonLat(coordinates))) {
      uavosInside.push(uavo);
    }
  }

  return uavosInside;
};

const returnProperPlaneParameter = planeData => {
  if (planeData.callsign) return planeData.callsign;
  if (planeData.registration) return planeData.registration;
  if (planeData.icao) return planeData.icao;
};

const returnProperPlaneParameterText = (planeData, subtitles) => {
  if (planeData.callsign) return subtitles.callsign_;
  if (planeData.registration) return subtitles.registration_;
  if (planeData.icao) return subtitles.icao_;
};

const deduplicateAircrafts = (drones, aircrafts) => {
  if (!drones.length || !aircrafts.length) return aircrafts;

  const droneIcaos = new Set();
  const droneCallsigns = new Set();
  const droneRegistrations = new Set();
  const droneIds = new Set();

  for (const drone of drones) {
    if (drone.icao) droneIcaos.add(drone.icao);
    if (drone.callsign) droneCallsigns.add(drone.callsign);
    if (drone.registration) droneRegistrations.add(drone.registration);
    if (drone.id) droneIds.add(drone.id);
  }

  return aircrafts.filter(aircraft => {
    if (aircraft.icao && droneIcaos.has(aircraft.icao)) return false;
    if (aircraft.callsign && droneCallsigns.has(aircraft.callsign)) return false;
    if (aircraft.registration && droneRegistrations.has(aircraft.registration))
      return false;
    if (aircraft.id && droneIds.has(aircraft.id)) return false;
    return true;
  });
};

const parseDrones = uavos => {
  const parsedUavos = [];
  for (const uavo of uavos) {
    let dbData = null;
    if (uavo.model) {
      try {
        const string = uavo.model.replace(/'/g, '"').replace(/None/g, null);
        dbData = JSON.parse(string);
      } catch (e) {
        console.log(e);
      }
    }
    parsedUavos.push({
      ...uavo,
      source: uavo.source.replace(/\s/g, ''),
      position: {
        type: 'Point',
        coordinates: uavo.position,
      },
      uid: dbData ? dbData.uid : uavo.id,
    });
  }

  return parsedUavos;
};

const handlePrepareUavoData = uavoData => {
  if (uavoData.type === 'drone') {
    if (uavoData.model) {
      try {
        const uavoExtraParameters = JSON.parse(
          uavoData.model?.replace(/'/g, '"').replace(/None/g, null)
        );
        delete uavoData.model;
        const newData = { ...uavoData, ...uavoExtraParameters };

        return newData;
      } catch (e) {
        console.log(e);
      }
    }

    return uavoData;
  }
  const { details } = uavoData;
  delete uavoData.details;

  return { ...uavoData, ...details };
};

export {
  setBasicUavoStyle,
  setBasicUavoIconStyle,
  setSelectedUavoStyle,
  updateSelectedVehicleData,
  getFeatureByIdFromLayer,
  showPopup,
  centerMapToCurrentUavoPosition,
  removeSelectedUavosIfDisapear,
  isUavoStillActive,
  basicUavoStyle,
  setUavoRotation,
  parseWeatherString,
  windDegreesParser,
  // returnProperPlaneParameter,
  // returnProperPlaneParameterText,
  parseDrones,
  deduplicateAircrafts,
  setIFFColorIfChanged,
  handlePrepareUavoData,
};
